import { ParsedQuery } from 'query-string';
import { NavigateOptions, To, useLocation, useNavigate, useParams } from 'react-router-dom';

import { parseQueries } from '@shared/utils/queries';
import { useCallback } from 'react';
import * as S from 'fp-ts/string';
import { User } from '@modules/users/model';
import { useProfile } from '@modules/profile/loader';
import { HttpStatusCode } from '@core/http';
import { ErrorResponse } from '@remix-run/router/utils';
import { Profile } from '@modules/profile/model';

export function useIdParam<I extends string>(key: string = 'id'): I {
  return useParams<{ [key: string]: I }>()[key] as I;
}

export function useTokenParam<I extends string>(): I {
  return useParams<{ token: I }>().token as I;
}

export function useQuery(name: string): string | null {
  return new URLSearchParams(useLocation().search).get(name);
}

export function useQueries(): ParsedQuery {
  return parseQueries(useLocation().search);
}

export function useHashDisclosure(hash: string): readonly [
  boolean,
  {
    readonly open: () => void;
    readonly close: () => void;
    readonly toggle: () => void;
  },
] {
  const location = useLocation();
  const navigate = useNavigate();

  const isOpen = location.hash === hash;

  const open = () => navigate({ ...location, hash });

  const close = () => navigate({ ...location, hash: undefined }, { replace: true });

  const toggle = () => (isOpen ? close() : open());

  return [isOpen, { open, close, toggle }];
}

export function usePreserveNavigate() {
  const location = useLocation();
  const navigate = useNavigate();

  const mergeToLocation = useCallback(
    (receivedTo: To): To => ({
      ...location,
      ...(S.isString(receivedTo) ? { pathname: receivedTo } : receivedTo),
    }),
    [location],
  );

  return (to: To, options?: NavigateOptions) => navigate(mergeToLocation(to), options);
}

export function useRestrictedProfile<Role extends User.Role>(role: Role): Profile<Role> {
  const profile = useProfile();

  if (role !== profile.role) {
    throw new ErrorResponse(
      HttpStatusCode.UNAUTHORIZED,
      'Authentication is required and has failed',
      new Error(`Route not allowed for role "${role}"`),
      true,
    );
  }

  return profile as Profile<Role>;
}
